import type { ShadCNComponents } from "@blocknote/shadcn"
import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger
} from "~/components/ui/dropdown-menu"
import type { ComponentProps, ReactNode } from "react"
import { cn } from "~/lib/utils"

/*
 * Maps BlockNote's shadcn DropdownMenu slots onto Orbit's shared dropdown-menu
 * components, so editor menus (drag handle, comments, tables, forms) render
 * with the app's popover surface and item styling. The add-block button and
 * drag handle itself stay BlockNote's own side menu buttons.
 *
 * Slot types in @blocknote/shadcn are base-ui primitives; these adapters sit
 * at that boundary and handle the two base-ui-isms BlockNote passes through:
 * - Trigger receives the side menu button as a `render` element prop instead
 *   of children.
 * - Content/SubContent receive a portal `container` (the editor's
 *   portalElement, which defaults to document.body) that the shared
 *   dropdown-menu components portaling to the body directly do not need.
 */

function MenuContent(
	props: ComponentProps<typeof DropdownMenuContent> & {
		container?: HTMLElement | null
	}
) {
	const { align = "start", container, className, ...contentProps } = props
	return (
		<DropdownMenuContent
			align={align}
			className={cn("min-w-56", className)}
			{...contentProps}
		/>
	)
}

function MenuTrigger({
	render,
	...triggerProps
}: ComponentProps<typeof DropdownMenuTrigger> & { render?: ReactNode }) {
	return (
		<DropdownMenuTrigger asChild {...triggerProps}>
			{render}
		</DropdownMenuTrigger>
	)
}

function MenuItem(
	props: ComponentProps<typeof DropdownMenuItem> & {
		icon?: ReactNode
		checked?: boolean
		subTrigger?: boolean
	}
) {
	const { icon, checked, subTrigger, className, children, ...itemProps } = props
	if (subTrigger)
		return (
			<>
				{icon}
				{children}
			</>
		)
	return checked === undefined ? (
		<DropdownMenuItem className={cn("px-2.5 py-2", className)} {...itemProps}>
			{icon}
			{children}
		</DropdownMenuItem>
	) : (
		<DropdownMenuCheckboxItem
			checked={checked}
			className={cn("px-2.5 py-2", className)}
			{...itemProps}
		>
			{icon}
			{children}
		</DropdownMenuCheckboxItem>
	)
}

function MenuSubContent(
	props: ComponentProps<typeof DropdownMenuSubContent> & {
		container?: HTMLElement | null
	}
) {
	const { align = "start", container, className, ...subContentProps } = props
	return (
		<DropdownMenuSubContent
			align={align}
			className={cn("min-w-56", className)}
			{...subContentProps}
		/>
	)
}

export const blocknoteMenuComponents = {
	DropdownMenu: {
		DropdownMenu,
		DropdownMenuTrigger: MenuTrigger,
		DropdownMenuContent: MenuContent,
		DropdownMenuGroup,
		DropdownMenuItem: MenuItem,
		DropdownMenuCheckboxItem,
		DropdownMenuLabel,
		DropdownMenuSeparator,
		DropdownMenuSub,
		DropdownMenuSubTrigger,
		DropdownMenuSubContent: MenuSubContent
	}
	// The slot registry is typed as base-ui primitives; our shared Radix-based
	// dropdown-menu components are structurally compatible at runtime but not
	// in those types, so cast once at this boundary.
} as unknown as Partial<ShadCNComponents>
